import { clone, omit } from 'ramda';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  GetSchedulesInfoDocument,
  GetSchedulesInfoQuery,
  ScheduleInfo,
  ScheduleJobStateType,
  ScheduleJobType,
  useGetScheduleJobsInProgressQuery,
  useGetScheduleJobsStateQuery,
} from '../graphql/components';
import { PATH_SCHEDULING_STUDIO_EDIT } from '../router/routes';
import { recordBy } from '../utils/array';
import { useAuth, Permissions } from './AuthorizationProvider';

const CHECK_STATUS_TIMEOUT = 5000;

export type ScheduleState = Pick<ScheduleJobType, 'correlationId' | 'state' | 'scheduleId'>;
type ScheduleInfoName = Pick<ScheduleInfo, 'name'>;
type ScheduleConfig = Omit<ScheduleState, 'scheduleId'> & ScheduleInfoName;
export type SchedulesStates = Record<string, ScheduleConfig>;

type BackgroundTasksStatusContextType = {
  autoScheduler: {
    addNewSchedulesToWatch: (schedulesConfig: ScheduleState[], schedulesInfo: Record<string, ScheduleInfoName>) => void;
    removeScheduleFromWatch: (scheduleId: string) => void;
    schedulesStates: SchedulesStates;
  };
};

const defaultBackgroundTasksStatusValues = {
  autoScheduler: {
    addNewSchedulesToWatch: () => null,
    removeScheduleFromWatch: () => null,
    schedulesStates: {},
  },
};

export const isScheduleJobRunning = (state: ScheduleJobStateType) =>
  [ScheduleJobStateType.InProgress, ScheduleJobStateType.InQueue].includes(state);

export const isScheduleJobDone = (state: ScheduleJobStateType) =>
  [ScheduleJobStateType.Failed, ScheduleJobStateType.UnlockFailed, ScheduleJobStateType.Completed].includes(state);

export const BackgroundTasksStatusContext = React.createContext<BackgroundTasksStatusContextType>(
  defaultBackgroundTasksStatusValues
);

type BackgroundTasksStatusProviderProps = {
  checkScheduleJobStatusesPollInterval?: number;
} & React.PropsWithChildren;

export const BackgroundTasksStatusProvider: React.FC<BackgroundTasksStatusProviderProps> = ({
  children,
  checkScheduleJobStatusesPollInterval = CHECK_STATUS_TIMEOUT,
}) => {
  const [schedulesStates, setSchedulesStates] = useState<SchedulesStates>({});
  const { t } = useTranslation();
  const { userHasPermissions } = useAuth();

  const userCanCheckForScheduleJob = userHasPermissions([Permissions.ViewScheduleJob]);

  const pendingScheduleCorrelationIds = useMemo(
    () =>
      Object.keys(schedulesStates)
        .filter((scheduleId) => isScheduleJobRunning(schedulesStates[scheduleId].state))
        .map((scheduleId) => schedulesStates[scheduleId].correlationId),
    [schedulesStates]
  );

  const addNewSchedulesToWatch = (
    schedulesConfig: ScheduleState[],
    schedulesInfo: Record<string, ScheduleInfoName>
  ) => {
    setSchedulesStates((previousState) => {
      const newState = clone(previousState);

      schedulesConfig.forEach(({ correlationId, scheduleId, state }) => {
        newState[scheduleId] = {
          correlationId,
          state,
          name: schedulesInfo[scheduleId].name ?? '',
        };
      });

      return newState;
    });
  };

  const removeScheduleFromWatch = (scheduleId: string) => {
    setSchedulesStates((previousState) => omit([scheduleId], previousState));
  };

  // initial query to load all schedules being generated
  const { client } = useGetScheduleJobsInProgressQuery({
    onCompleted: async (data) => {
      try {
        const scheduleIds = data.getScheduleJobsInProgress.map(({ scheduleId }) => scheduleId);

        if (scheduleIds.length) {
          const schedules = await client.query<GetSchedulesInfoQuery>({
            query: GetSchedulesInfoDocument,
            variables: { scheduleIds: scheduleIds.toString() },
          });

          addNewSchedulesToWatch(data?.getScheduleJobsInProgress ?? [], recordBy(schedules.data.schedulesInfo, 'id'));
        }
      } catch (e) {
        // error message provided by response provider
      }
    },
    skip: !userCanCheckForScheduleJob,
  });

  const updateSchedulesStates = (scheduleStates: ScheduleJobType[]) => {
    setSchedulesStates((previousState) => {
      const newState = clone(previousState);

      scheduleStates.forEach(({ scheduleId, state, code }) => {
        if (newState[scheduleId].state !== state) {
          // do not show toasts for IN_QUEUE and IN_PROGRESS states
          if (isScheduleJobDone(state)) {
            const scheduleName = newState[scheduleId].name;
            const codeAsNumber = Number.parseInt(code, 10);
            const reasonMessageKey = `schedule.autoSchedule.generationCode.${code}`;
            const reasonMessageText = t(reasonMessageKey);

            // show unknown reason as fallback (status code 300)
            const reasonMessage =
              reasonMessageText === reasonMessageKey
                ? t(`schedule.autoSchedule.generationCode.300`)
                : reasonMessageText;
            const linkToSchedule = `${PATH_SCHEDULING_STUDIO_EDIT}/${scheduleId}`;

            if (codeAsNumber < 200) {
              toast.success(
                <Trans i18nKey="schedule.autoSchedule.generationWasSuccessful" values={{ scheduleName }}>
                  {/* space needs to be added within strong tag so the tests can run correctly */}
                  Auto Schedule of <Link to={linkToSchedule}>Name of schedule</Link> has been successfully finished
                </Trans>
              );
            } else if (codeAsNumber < 300) {
              toast.warning(
                <Trans
                  i18nKey="schedule.autoSchedule.generationHasFinishedWithWarning"
                  values={{ scheduleName, reason: reasonMessage }}
                >
                  {/* space needs to be added within strong tag so the tests can run correctly */}
                  Auto Schedule of <Link to={linkToSchedule}>Name of schedule</Link> has finished with warning: schedule
                  was not unlocked
                </Trans>
              );
            } else {
              toast.error(
                <Trans
                  i18nKey="schedule.autoSchedule.generationHasFailed"
                  values={{ scheduleName, reason: reasonMessage }}
                >
                  {/* space needs to be added within strong tag so the tests can run correctly */}
                  Auto Schedule of <Link to={linkToSchedule}>Name of schedule</Link> has failed due to unexpected reason
                </Trans>
              );
            }
          }

          newState[scheduleId].state = state;
        }
      });

      return newState;
    });
  };

  // query that periodically check statuses of pending schedules
  useGetScheduleJobsStateQuery({
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    onCompleted: (data) => updateSchedulesStates(data?.getScheduleJobsState ?? []),
    pollInterval: pendingScheduleCorrelationIds.length ? checkScheduleJobStatusesPollInterval : 0,
    skip: !pendingScheduleCorrelationIds.length,
    variables: pendingScheduleCorrelationIds.length
      ? {
          correlationId: pendingScheduleCorrelationIds.toString(),
        }
      : undefined,
  });

  return (
    <BackgroundTasksStatusContext.Provider
      value={{
        autoScheduler: {
          addNewSchedulesToWatch,
          removeScheduleFromWatch,
          schedulesStates,
        },
      }}
    >
      {children}
    </BackgroundTasksStatusContext.Provider>
  );
};

export const BackgroundTasksStatusConsumer = BackgroundTasksStatusContext.Consumer;

export const useBackgroundTasksStatus = () => React.useContext(BackgroundTasksStatusContext);
